// Apolo IDE — LearningPanel Candidates sub-tab (WS L6.2).
//
// Lists pending learning candidates from /api/learning/candidates, lets the
// user approve / reject / dismiss them. Approve triggers the Worker-side
// curator (memory or skill writer); the renderer-side vaultCommit pump
// picks it up from there.

import { useState, useEffect, useRef } from 'react';
import { Check, Loader2, X, Archive, BookOpen } from 'lucide-react';
import {
  useLearningCandidates,
  approveCandidate,
  rejectCandidate,
  dismissCandidate,
} from '../../../../hooks/useLearning.js';
import { readSkillPreviewEnabled } from '../../../../lib/flags/sprintFlags.js';
import MarkdownMessage from '../../../MarkdownMessage.jsx';

const TYPE_TONES = {
  memory: 'bg-blue-900/40 text-blue-200',
  skill: 'bg-violet-900/40 text-violet-200',
  nudge: 'bg-amber-900/40 text-amber-200',
};

function confidencePct(c) {
  if (typeof c !== 'number' || Number.isNaN(c)) return '—';
  return `${Math.round(c * 100)}%`;
}

export default function Candidates() {
  const { data, loading, error, refetch } = useLearningCandidates('pending', {
    pollIntervalMs: 15_000,
  });
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const autoApprovedIds = useRef(new Set());
  // E5 (super-marathon) — readable SKILL.md preview (flag OFF ⇒ never set, raw <details> shows).
  const previewEnabled = readSkillPreviewEnabled();
  const [preview, setPreview] = useState(null); // { id, content } | null

  useEffect(() => {
    const candidates = data?.data?.candidates ?? data?.candidates ?? [];
    const pending = candidates.filter(
      (c) => c.candidate_type === 'memory' && !autoApprovedIds.current.has(c.id),
    );
    if (pending.length === 0) return;
    pending.forEach((c) => autoApprovedIds.current.add(c.id));
    Promise.all(pending.map((c) => approveCandidate(c.id))).then(() => refetch());
  }, [data, refetch]);

  async function run(action, id) {
    setBusyId(id);
    setActionError(null);
    const fn = action === 'approve' ? approveCandidate : action === 'reject' ? rejectCandidate : dismissCandidate;
    const res = await fn(id);
    setBusyId(null);
    if (!res.ok) {
      setActionError(res.error || `${action} failed`);
      return;
    }
    refetch();
  }

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 p-3 text-[11px] text-apolo-textTertiary" role="status">
        <Loader2 size={12} className="animate-spin" aria-hidden="true" />
        Carregando candidates...
      </div>
    );
  }
  if (error) {
    return (
      <div role="alert" className="p-3 text-[11px] text-apolo-error">
        {error}
      </div>
    );
  }

  const candidates = data?.data?.candidates ?? data?.candidates ?? [];

  if (candidates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-1 p-6 text-[11px] text-apolo-textTertiary">
        <p>Nenhum candidate pendente.</p>
        <p className="text-[10px]">Os patterns detectados aparecem aqui.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-3">
      {actionError && (
        <div role="alert" className="rounded border border-apolo-error/30 bg-apolo-error/10 px-2 py-1 text-[10px] text-apolo-error">
          {actionError}
        </div>
      )}
      <ul aria-label="Candidates pendentes" className="flex flex-col gap-2">
        {candidates.map((c) => {
          const tone = TYPE_TONES[c.candidate_type] || 'bg-apolo-surface2 text-apolo-textSecondary';
          const isBusy = busyId === c.id;
          return (
            <li key={c.id} className="rounded border border-apolo-borderSubtle bg-apolo-surface2 p-2">
              <div className="mb-1 flex items-center justify-between gap-2">
                <span className={`rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${tone}`}>
                  {c.candidate_type}
                </span>
                <span className="text-[10px] text-apolo-textTertiary">{confidencePct(c.confidence)} conf</span>
              </div>
              <p className="text-[11px] leading-snug text-apolo-textPrimary">{c.summary}</p>
              {c.proposed_content && (previewEnabled ? (
                <button
                  type="button"
                  onClick={() => setPreview({ id: c.id, content: c.proposed_content })}
                  aria-label={`Ver o SKILL.md legível do candidate ${c.id}`}
                  className="mt-1 inline-flex items-center gap-1 text-[10px] text-apolo-textTertiary transition hover:text-apolo-accent"
                >
                  <BookOpen size={10} aria-hidden="true" />
                  Ver legível
                </button>
              ) : (
                <details className="mt-1">
                  <summary className="cursor-pointer text-[10px] text-apolo-textTertiary hover:text-apolo-textSecondary">
                    Ver conteúdo proposto
                  </summary>
                  <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap rounded bg-apolo-bg p-1 text-[10px] text-apolo-textSecondary">
                    {c.proposed_content}
                  </pre>
                </details>
              ))}
              <div className="mt-2 flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => run('approve', c.id)}
                  disabled={isBusy}
                  aria-label={`Aprovar candidate ${c.id}`}
                  className="inline-flex items-center gap-1 rounded border border-apolo-borderSubtle bg-apolo-surface1 px-2 py-1 text-[10px] text-apolo-success transition hover:border-apolo-success disabled:opacity-50"
                >
                  {isBusy ? <Loader2 size={10} className="animate-spin" aria-hidden="true" /> : <Check size={10} aria-hidden="true" />}
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => run('reject', c.id)}
                  disabled={isBusy}
                  aria-label={`Rejeitar candidate ${c.id}`}
                  className="inline-flex items-center gap-1 rounded border border-apolo-borderSubtle bg-apolo-surface1 px-2 py-1 text-[10px] text-apolo-error transition hover:border-apolo-error disabled:opacity-50"
                >
                  <X size={10} aria-hidden="true" />
                  Reject
                </button>
                <button
                  type="button"
                  onClick={() => run('dismiss', c.id)}
                  disabled={isBusy}
                  aria-label={`Descartar candidate ${c.id}`}
                  className="inline-flex items-center gap-1 rounded border border-apolo-borderSubtle bg-apolo-surface1 px-2 py-1 text-[10px] text-apolo-textTertiary transition hover:text-apolo-textPrimary disabled:opacity-50"
                >
                  <Archive size={10} aria-hidden="true" />
                  Dismiss
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      {previewEnabled && preview && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="SKILL.md legível"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setPreview(null)}
        >
          <div
            className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-apolo-lg border border-apolo-borderSubtle bg-apolo-surface1 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-apolo-borderSubtle px-4 py-2">
              <h3 className="text-sm font-medium text-apolo-textPrimary">SKILL.md — pré-visualização</h3>
              <button
                type="button"
                onClick={() => setPreview(null)}
                aria-label="Fechar pré-visualização"
                className="rounded p-1 text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
              >
                <X size={14} aria-hidden="true" />
              </button>
            </div>
            <div className="overflow-auto px-4 py-3 text-sm">
              <MarkdownMessage content={preview.content} highlightSkills={false} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
